/* Replace ONLY the existing PredictiveSearch/SearchDrawer/ProductDrawer block in the theme asset.
   Shopify native predictive endpoint: one request for products + query suggestions.
   Product tag filtering remains in snippets/predictive-search.liquid. */
class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.requestController = null;
    this.requestURL = '';
    this.searchSequence = 0;
    this.timer = null;
    this.focusElement = this.input;
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    this.resetButton?.addEventListener('click', (e) => this.clear(e));
    this.input?.addEventListener('input', () => this.queueSearch());
    this.input?.addEventListener('focus', () => this.onFocus());

    if (this.closest('.template-search')) {
      document.addEventListener('click', (e) => this.handleClickOutside(e));
    }
  }

  get input() { return this.querySelector('input[type="search"]'); }
  get resetButton() { return this.querySelector('button[type="reset"]'); }
  getQuery() { return this.input.value.trim(); }

  get resultTarget() {
    return this.querySelector('[id^="PredictiveSearchResults-"]');
  }

  onFocus() {
    if (!this.closest('.template-search')) return;
    document.body.classList.add('predictive-search-open');
    if (this.getQuery() && !this.hasAttribute('results')) this.queueSearch(true);
  }

  setupURL() {
    const base = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`, window.location.origin);
    base.searchParams.set('q', this.getQuery());
    base.searchParams.set('resources[type]', 'product,query');
    base.searchParams.set('resources[limit]', '10');
    base.searchParams.set('resources[limit_scope]', 'each');
    base.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return base;
  }

  queueSearch(immediate = false) {
    clearTimeout(this.timer);
    this.searchSequence += 1;
    if (this.requestController) this.requestController.abort();
    this.requestController = null;
    this.requestURL = '';

    if (!this.getQuery()) {
      this.clear();
      return;
    }
    this.removeAttribute('results');
    this.setAttribute('loading', 'true');
    this.resetButton?.classList.add('btn--loading');
    this.searchContent?.classList.remove('hidden');
    if (this.resultTarget) {
      this.resultTarget.innerHTML = '<p class="ns-fast-loading" role="status">Searching products...</p>';
    }

    const seq = this.searchSequence;
    this.timer = setTimeout(() => this.performSearch(seq), immediate ? 0 : 110);
  }

  onChange() { this.queueSearch(); }

  async performSearch(seq) {
    if (seq !== this.searchSequence || !this.getQuery()) return;
    const url = this.setupURL().toString();
    if (this.cachedMap.has(url)) {
      this.renderSearchResults(this.cachedMap.get(url));
      this.finishSearch(seq);
      return;
    }

    const controller = new AbortController();
    this.requestController = controller;
    this.requestURL = url;
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Predictive search HTTP ' + response.status);
      const html = await response.text();
      if (seq !== this.searchSequence || controller.signal.aborted) return;
      this.cachedMap.set(url, html);
      if (this.cachedMap.size > 25) this.cachedMap.delete(this.cachedMap.keys().next().value);
      this.renderSearchResults(html);
    } catch (error) {
      if (error.name !== 'AbortError' && seq === this.searchSequence) {
        console.error('Predictive search:', error);
        if (this.resultTarget) this.resultTarget.textContent = 'Search could not load. Please try again.';
      }
    } finally {
      if (seq === this.searchSequence) this.finishSearch(seq);
    }
  }

  finishSearch(seq) {
    if (seq !== this.searchSequence) return;
    this.removeAttribute('loading');
    this.resetButton?.classList.remove('btn--loading');
    this.setAttribute('results', 'true');
  }

  renderSearchResults(responseText) {
    const target = this.resultTarget;
    if (!target) return;
    const doc = new DOMParser().parseFromString(responseText, 'text/html');
    const source = doc.getElementById(target.id);
    if (!source) {
      console.warn('Predictive search results element missing:', target.id);
      return;
    }
    this.searchContent?.classList.remove('hidden');
    target.innerHTML = source.innerHTML;
    // Score only products Shopify returned. This is not a full catalogue search.
    const list = target.querySelector('#predictive-search-results-products-list');
    if (list) {
      const normalized = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      const terms = normalized(this.getQuery()).split(/\s+/).filter(Boolean);
      const score = (li) => {
        const t = normalized(li.querySelector('.text-pcard-title')?.textContent || '');
        const titleWords = t.split(/\s+/);
        let hits = 0;
        for (const word of terms) if (titleWords.some(w => w.startsWith(word))) hits++;
        return (hits === terms.length ? 1000 : 0) + hits * 100 + (t.includes(terms.join(' ')) ? 200 : 0);
      };
      const items = [...list.children];
      items.sort((a, b) => score(b) - score(a));
      list.replaceChildren(...items);
    }
  }

  clear(event = null) {
    event?.preventDefault();
    clearTimeout(this.timer);
    this.searchSequence += 1;
    this.requestController?.abort();
    this.requestController = null;
    this.requestURL = '';
    this.input.value = '';
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.resetButton?.classList.remove('btn--loading');
    if (this.resultTarget) this.resultTarget.replaceChildren();
    if (this.searchRecommendationEmpty) this.searchContent?.classList.add('hidden');
    this.input.focus();
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => document.body.classList.remove('predictive-search-open'), 0);
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  get requiresBodyAppended() { return false; }
  get input() { return this.querySelector('input[type="search"]'); }
  get focusElement() { return this.input; }
}
customElements.define('search-drawer', SearchDrawer);

class ProductDrawer extends DrawerComponent {
  get requiresBodyAppended() { return false; }
  get focusElement() { return this.querySelector('.drawer__content'); }
}
customElements.define('product-drawer', ProductDrawer);
